import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Table, 
    Button, 
    Space, 
    Input, 
    Select, 
    Card, 
    Typography, 
    Tooltip,
    Breadcrumb
} from 'antd';
import { 
    PlusOutlined, 
    SearchOutlined, 
    EditOutlined, 
    FilterOutlined,
    ReloadOutlined,
    BankOutlined,
    WalletOutlined,
    SwapOutlined
} from '@ant-design/icons';
import { useTransaccs } from '../hooks/useTransaccs';
import {TransaccDTO} from "@/shared/api/model";
import {ColumnsType} from "antd/es/table";

const { Title } = Typography;
const { Option } = Select;

const TransaccManagementView: React.FC = () => {
    const navigate = useNavigate();
    const { 
        transaccs, 
        isLoading, 
        filterByAcctype, 
        resetFilters, 
        loadTransaccs,
        getAcctypeLabel
    } = useTransaccs();
    
    const [searchText, setSearchText] = useState('');
    const [filteredData, setFilteredData] = useState<TransaccDTO[]>([]);
    
    // Update filtered data when transaccs or search text changes
    useEffect(() => {
        if (!transaccs) return;

        if (!searchText) {
            setFilteredData(transaccs);
            return;
        }

        const lowercaseSearch = searchText.toLowerCase();
        const filtered = transaccs.filter(item =>
            (item.glNo?.toLowerCase().includes(lowercaseSearch) ?? false) ||
            (item.acNo?.toLowerCase().includes(lowercaseSearch) ?? false) ||
            (item.acTitle?.toLowerCase().includes(lowercaseSearch) ?? false)
        );

        setFilteredData(filtered);
    }, [transaccs, searchText]);
    
    // Handle creating a new account
    const handleCreateNew = () => {
        navigate('/accounts/transacc/new');
    };
    
    // Handle editing an account
    const handleEdit = (record: TransaccDTO) => {
        navigate(`/accounts/transacc/edit/${record.glNo}/${record.acNo}`);
    };
    
    // Get icon based on account type
    const getAcctypeIcon = (acctype: number) => {
        switch (acctype) {
            case 0: return <WalletOutlined style={{ color: '#52c41a' }} />;
            case 1: return <BankOutlined style={{ color: '#1890ff' }} />;
            case 2: return <SwapOutlined style={{ color: '#fa8c16' }} />;
            default: return null;
        }
    };
    
    // Table columns
    // Table columns with null-safe sorting
    const columns = [
        {
            title: 'Type',
            dataIndex: 'acctype',
            key: 'acctype',
            width: 80,
            render: (acctype: number) => (
                <Tooltip title={getAcctypeLabel(acctype)}>
                    {getAcctypeIcon(acctype)}
                </Tooltip>
            ),
            filters: [
                { text: 'Cash', value: 0 },
                { text: 'Bank', value: 1 },
                { text: 'Transfer', value: 2 }
            ],
            onFilter: (value: boolean | React.Key, record: TransaccDTO) => record.acctype === (value as number)
        },
        {
            title: 'GL Number',
            dataIndex: 'glNo',
            key: 'glNo',
            sorter: (a: TransaccDTO, b: TransaccDTO) => (a.glNo ?? '').localeCompare(b.glNo ?? '')
        },
        {
            title: 'Account Number',
            dataIndex: 'acNo',
            key: 'acNo',
            sorter: (a: TransaccDTO, b: TransaccDTO) => (a.acNo ?? '').localeCompare(b.acNo ?? '')
        },
        {
            title: 'Account Title',
            dataIndex: 'acTitle',
            key: 'acTitle',
            sorter: (a: TransaccDTO, b: TransaccDTO) => (a.acTitle ?? '').localeCompare(b.acTitle ?? '')
        },
        {
            title: 'Account Type',
            dataIndex: 'acctype',
            key: 'acctypeLabel',
            render: (acctype: number) => getAcctypeLabel(acctype)
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_: any, record: TransaccDTO) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                </Space>
            )
        }
    ];
    
    return (
        <div className="p-4">
            <Breadcrumb className="mb-4">
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item>Accounts</Breadcrumb.Item>
                <Breadcrumb.Item>Bank/Cash/Transfer Accounts</Breadcrumb.Item>
            </Breadcrumb>
            
            <Card>
                <div className="flex justify-between items-center mb-4">
                    <Title level={4}>
                        <Space>
                            <BankOutlined />
                            Bank/Cash/Transfer Accounts
                        </Space>
                    </Title>
                    
                    <Space>
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={handleCreateNew}
                        >
                            Create New
                        </Button>
                    </Space>
                </div>
                
                <div className="flex justify-between items-center mb-4">
                    <Space>
                        <Input
                            placeholder="Search accounts..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: 250 }}
                            allowClear
                        />
                        
                        <Select
                            placeholder="Filter by type"
                            style={{ width: 150 }}
                            allowClear
                            onChange={(value) => filterByAcctype(value)}
                        >
                            <Option value={0}>Cash</Option>
                            <Option value={1}>Bank</Option>
                            <Option value={2}>Transfer</Option>
                        </Select>
                    </Space>
                    
                    <Space>
                        <Button 
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                resetFilters();
                                setSearchText('');
                                loadTransaccs();
                            }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey={(record) => `${record.glNo ?? ''}-${record.acNo ?? ''}`}
                    loading={isLoading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        </div>
    );
};

export default TransaccManagementView;
